// Host-side Workspace wrapper.
//
// Runs inside a Cloudflare Worker / Durable Object. Owns a local
// dofs Database (the host store) and a SyncRPC connection
// to computerd. Filesystem operations on Workspace.fs mutate the local
// store directly via the WorkspaceFilesystem class from
// @cloudflare/dofs; sync between the host store and computerd
// is driven explicitly via Workspace.push() / Workspace.pull().
// Command-backend pre-exec push / post-exec pull brackets are
// routed through Workspace.runtime.exec.

import type { ShellRPC } from "@cloudflare/computer-rpc";
import {
  captureSyncTarget,
  pullBlocks,
  pullOnce,
  pushBlocks,
  pushOnce,
  reconcileWatermarks,
  type SyncProgress,
} from "@cloudflare/computer-rpc/driver";
import {
  type ApplyResult,
  type ChangeCursor,
  Database,
  type DurableObjectStorageLike,
  initializeSchema,
  SQLiteWorkspaceProvider,
  WorkspaceFilesystem,
} from "@cloudflare/dofs";
import { ARTIFACTS_NOT_CONFIGURED_MESSAGE } from "./artifacts/errors.js";
import {
  type ArtifactClient,
  ArtifactError,
  createArtifact,
  runArtifactsCLI,
} from "./artifacts/index.js";
import type { AssetsClient } from "./assets/index.js";
import type { BackendHandle, WorkspaceBackend } from "./backend.js";
import { ExecutionRuntimeTracker, SqlExecutionRuntimeStore } from "./execution-runtime-tracker.js";
import type { GitClient, GitClientFactory, GitIdentity } from "./git/index.js";
import { MountIndex } from "./mounts/index.js";
import { buildMountRegistry, type MountValue } from "./mounts/registry.js";
import type { Mount } from "./mounts/types.js";
import { noopObserver, safeErrorMessage, type WorkspaceObserver, withSpan } from "./observe.js";
import { createSyncLogger, type SyncLogger } from "./observe/sync-telemetry.js";
import {
  DEFAULT_OUTPUT_DIR,
  DEFAULT_OUTPUT_KEEP,
  parseOutputOptions,
  WorkspaceCommandOutputFiles,
  type WorkspaceOutputOptions,
} from "./runtime/output-files.js";
import { makeOutputLimits } from "./runtime/output-tail.js";
import { WorkspaceRuntime } from "./runtime/runtime.js";
import {
  isModuleBackend,
  type WorkspaceModuleBackend,
  type WorkspaceModuleBackendHandle,
  type WorkspaceRegisteredBackend,
  type WorkspaceRuntimeEvent,
} from "./runtime/types.js";
import { CommandExecutor, maybeDispose, spawnShell } from "./shell.js";
import { WorkspaceStub } from "./stub.js";
import {
  isWorkspacePreDispatchTransportFailure,
  isWorkspaceTransportFailure,
  WorkspacePreDispatchTransportError,
  WorkspaceTransportError,
} from "./transport-failure.js";

// When a backend RPC fails with a transport error, how much replay
// the operation tolerates. "always" suits idempotent calls; a
// "pre-dispatch" operation is replayed only when the failure proves
// no frame reached the peer.
type BackendRetryPolicy = "always" | "pre-dispatch";

export interface WorkspaceOptions {
  // Local store backing this Workspace. In a Durable Object, pass
  // `ctx.storage`; in tests, pass a SQLiteTestStorage from
  // @cloudflare/dofs/testing. The constructor opens a
  // Database against it and runs initializeSchema (idempotent).
  storage: DurableObjectStorageLike;

  // Registered execution backends. The first is the default;
  // callers can select another by stable id.
  // Omit to construct a filesystem-only Workspace whose runtime
  // reports that no execution backend is configured.
  backends?: WorkspaceRegisteredBackend[];

  // Clock used for mtime / last_seen on local FS writes. Defaults
  // to Date.now. Override for deterministic tests.
  now?: () => number;

  // Identifier for this workspace / session. Forwarded to mount
  // factories via MountContext.sessionId. Optional; defaults to "".
  sessionId?: string;

  // Mounts to register against the workspace. Keys are absolute
  // mount roots (no trailing slash, no nesting). Values are either
  // bare Mount objects or factories that take a MountContext and
  // return one. Factories are called once at construction.
  mounts?: Record<string, MountValue>;

  // Observer that receives one span per workspace operation: a
  // `workspace.connect` per backend connect attempt,
  // `workspace.sync.push` / `workspace.sync.pull` per sync call,
  // command runtime spans per exec, and `workspace.fs.<op>` per
  // filesystem call routed through the stub. The default is a
  // no-op so the package has no observability cost when callers
  // do not opt in. See `./observe.ts` for the contract and the
  // adapter subpaths for the Cloudflare runtime and OpenTelemetry.
  observer?: WorkspaceObserver;

  // Structured per-block sync telemetry, emitted as single-line JSON so
  // the Workers Observability query API can aggregate it into the CPU
  // headroom and transport throughput numbers local benchmarks can only
  // model. See observe/sync-telemetry.ts for the queries.
  //
  // Off by default. Workers Logs is billed per event and a large sync
  // emits one record per block, so writing to a consumer's log stream
  // uninvited would be both surprising and metered. Set
  // syncTelemetryEnabled to opt in, or pass syncTelemetry to route the
  // records somewhere else.
  syncTelemetry?: SyncLogger;
  syncTelemetryEnabled?: boolean;

  // Optional git client factory. Omit it to keep the default
  // Workspace graph free of isomorphic-git; pass createGitClient()
  // from @cloudflare/computer/git when the caller needs
  // workspace.git or the worker backend's built-in git command.
  git?: WorkspaceGitFactory;

  // Default identity used by commit-producing git subcommands
  // when neither the call site nor the relevant `GIT_AUTHOR_*` /
  // `GIT_COMMITTER_*` env vars supply one. Threaded through to
  // the configured git factory on first access to `workspace.git`.
  defaultGitIdentity?: GitIdentity;

  // Optional assets publisher used by WorkspaceStub and the worker
  // backend's `assets publish` shell command. Pass an AssetsClient
  // directly, or a factory when the publisher needs the Workspace
  // instance itself (for example, createAssets({ ws, ... })).
  assets?: AssetsClient | ((ws: Workspace) => AssetsClient);

  // Optional Cloudflare Artifacts binding. When configured,
  // `workspace.artifacts` is an Artifacts client. When omitted,
  // accessing the client is still possible but every operation
  // fails with a clear configuration error.
  //
  // The client is scoped to a session when one is available: the
  // session id defaults to WorkspaceOptions.sessionId, and
  // `artifacts.sessionId` overrides it. With neither — or with an
  // explicit `artifacts.sessionId: null`, the opt-out for a
  // workspace that has a session id but wants artifacts across
  // every session — the client spans the whole namespace: it lists
  // and reaches every repository, including those other sessions
  // own. Scope it unless the caller is meant to administer the
  // namespace.
  //
  // An omitted or undefined session id intentionally asks for the
  // namespace-wide client. An empty string is a construction error
  // instead, so blank input or code that normalizes a missing value
  // to "" cannot widen one tenant's client to all of them.
  artifacts?: {
    binding: Artifacts;
    sessionId?: string | null;
  };

  // How much command output a run's result keeps, and where the rest
  // goes. Output past the limits is cut to its last lines, like pi's
  // bash tool, and saved in full to a file under `dir`, newest `keep`
  // files kept. Defaults: 2000 lines, 64 KiB, `/.computer/output`, 50
  // files. `false` keeps all output in results and saves nothing.
  output?: WorkspaceOutputOptions | false;
}

export type WorkspaceGitFactory = GitClientFactory;

const GIT_NOT_CONFIGURED_MESSAGE =
  "Workspace git is not configured. Import createGitClient from " +
  "@cloudflare/computer/git and pass createGitClient() as WorkspaceOptions.git.";

const DISABLED_GIT_CLIENT = new Proxy(
  {},
  {
    get(_target, property) {
      if (property === "then") return undefined;
      return () => Promise.reject(new Error(GIT_NOT_CONFIGURED_MESSAGE));
    },
  },
) as GitClient;

export class Workspace {
  readonly #db: Database;
  readonly #fs: WorkspaceFilesystem;
  /**
   * Lazily-constructed dofs provider. Built on first `provider()`
   * call; cached so repeated callers share the same instance.
   */
  #provider: SQLiteWorkspaceProvider | undefined;
  readonly #backends: WorkspaceBackend[];
  readonly #backendsById: Map<string, WorkspaceBackend>;
  readonly #moduleBackendsById: Map<string, WorkspaceModuleBackend>;
  readonly #registeredBackends: Map<string, WorkspaceRegisteredBackend>;
  readonly #output: WorkspaceOutputOptions | false;
  readonly #defaultBackendId: string | undefined;
  readonly #observer: WorkspaceObserver;
  readonly #syncLogger: SyncLogger;
  readonly #now: () => number;
  readonly #sessionId: string;
  readonly #gitFactory: WorkspaceGitFactory | undefined;
  readonly #defaultGitIdentity: GitIdentity | undefined;
  readonly #assets: AssetsClient | undefined;
  readonly #artifacts: ArtifactClient;
  // Lazily-constructed git client, cached so the dynamic
  // imports of isomorphic-git / diff land once per Workspace.
  #git: GitClient | undefined;
  readonly #mounts: Map<string, Mount>;
  readonly #mountIndex: MountIndex;
  // Per-backend handle cache. Filled lazily on first use of each
  // backend; a closed transport drops just that backend's entry,
  // leaving the others warm.
  readonly #handles = new Map<string, BackendHandle>();
  // In-flight connect promises keyed by backend id, so concurrent
  // callers for the same backend share one connect pass.
  readonly #connecting = new Map<string, Promise<BackendHandle>>();
  // A transport-failed handle must finish closing before connect()
  // runs again. This prevents a concurrent caller from reaching a
  // backend's own cache while it still points at the stale handle.
  readonly #disconnecting = new Map<string, Promise<void>>();
  // Per-backend CommandExecutor wrappers. Constructed alongside each
  // handle; reused for the life of the handle.
  readonly #shells = new Map<string, CommandExecutor>();
  // Cached command adapters presenting a CommandExecutor as the
  // unified backend handle. Cleared alongside #shells so an adapter
  // never outlives the shell it wraps.
  readonly #commandHandles = new Map<string, WorkspaceModuleBackendHandle>();
  // Last known container runtime for recent backend/execution ids.
  // Returned handles carry their own id; the bounded LRU supports
  // direct by-id lifecycle calls without growing for the DO lifetime.
  readonly #executionRuntimes: ExecutionRuntimeTracker;
  readonly #moduleHandles = new Map<string, WorkspaceModuleBackendHandle>();
  readonly #connectingModuleHandles = new Map<string, Promise<WorkspaceModuleBackendHandle>>();
  #connectionGeneration = 0;
  #runtime: WorkspaceRuntime | undefined;
  #readyPromise: Promise<void> | undefined;
  // Per-backend FIFOs that serialize mutating entry points (push,
  // pull, and the shell exec bracket which goes through them) for
  // that backend. A push to backend A does not block exec on
  // backend B. Reads bypass the queue entirely — they hit the
  // local store directly through Workspace.fs. Each value is a
  // single tail-promise; each caller chains its work onto the tail
  // and updates it. See docs/02 "Concurrent mutators".
  readonly #mutationTails = new Map<string, Promise<unknown>>();

  constructor(options: WorkspaceOptions) {
    this.#now = options.now ?? Date.now;
    this.#sessionId = options.sessionId ?? "";
    this.#gitFactory = options.git;
    this.#defaultGitIdentity = options.defaultGitIdentity;
    this.#artifacts = options.artifacts
      ? createArtifact(options.artifacts.binding, artifactsSessionId(options))
      : createDisabledArtifactsClient();
    this.#db = new Database(options.storage);
    initializeSchema(this.#db, this.#now);
    this.#executionRuntimes = new ExecutionRuntimeTracker(
      1_024,
      new SqlExecutionRuntimeStore(this.#db),
    );
    this.#fs = new WorkspaceFilesystem(this.#db, { now: this.#now });
    const registered = (options.backends ?? []).slice();
    this.#backends = registered.filter(
      (backend): backend is WorkspaceBackend => !isModuleBackend(backend),
    );
    this.#backendsById = new Map(this.#backends.map((backend) => [backend.id, backend]));
    this.#moduleBackendsById = new Map(
      registered.filter(isModuleBackend).map((backend) => [backend.id, backend]),
    );
    this.#registeredBackends = new Map();
    this.#output = parseOutputOptions(options.output);
    for (const backend of registered) {
      if (this.#registeredBackends.has(backend.id)) {
        throw new Error(
          `Workspace: duplicate backend id ${JSON.stringify(backend.id)}. ` +
            "Pass an explicit `id` on each backend's constructor options to " +
            "distinguish them.",
        );
      }
      this.#registeredBackends.set(backend.id, backend);
    }
    this.#defaultBackendId = registered[0]?.id;
    this.#observer = options.observer ?? noopObserver;
    this.#syncLogger =
      options.syncTelemetry ?? createSyncLogger({ enabled: options.syncTelemetryEnabled ?? false });
    this.#mounts = buildMountRegistry(options.mounts, {
      sessionId: options.sessionId,
      vfs: () => this.provider(),
    });
    this.#mountIndex = new MountIndex({
      db: this.#db,
      fs: this.#fs,
      mounts: this.#mounts,
    });
    this.#assets = typeof options.assets === "function" ? options.assets(this) : options.assets;
  }

  // Force every registered mount to materialize. Idempotent; safe to
  // call from multiple places (ready(), tests, future fs/shell
  // entry points). Concurrent callers share one materialize() pass
  // per mount.
  ensureMountsIndexed(): Promise<void> {
    return this.#mountIndex.ensureIndexed();
  }

  // Resolved mount registry, keyed by absolute mount root. Returned
  // as a defensive copy so callers can't mutate the internal map.
  mounts(): Map<string, Mount> {
    return new Map(this.#mounts);
  }

  // Local store. Exposed for tests / diagnostics and for the
  // sync helpers that take a Database directly.
  get db(): Database {
    return this.#db;
  }

  // Observer used to wrap workspace operations in spans. Exposed for the
  // stub and shell wrappers, which put their own entry points in
  // spans named after the boundary the caller crossed. Defaults to a
  // no-op when the constructor did not receive one.
  get observer(): WorkspaceObserver {
    return this.#observer;
  }

  // Filesystem wrapper — the documented Workspace.fs surface from
  // docs/04. Available immediately; doesn't need ready() because
  // reads and writes hit the local store, not the wire.
  //
  // Read-only mount enforcement lives at the data layer in
  // @cloudflare/dofs: writeFile / mkdir / rm consult the registered
  // mount roots and reject EROFS without needing a workspace-side
  // wrapper. The same check fires on the apply path used by
  // pullOnce, so container-side writes under a read-only mount are
  // also rejected (and surfaced via Workspace.pull's skipped[]).
  get fs(): WorkspaceFilesystem {
    return this.#fs;
  }

  // Identifier for this workspace / session, as passed to the
  // constructor. Empty string when the caller did not supply one.
  // Forwarded to mount factories and used by the assets module to
  // tag shared objects with their originating session.
  get sessionId(): string {
    return this.#sessionId;
  }

  // Optional assets publisher. Exposed through WorkspaceStub so
  // the worker backend's shell can run `assets publish` without
  // receiving R2 bindings or signing secrets in the Dynamic Worker.
  get assets(): AssetsClient | undefined {
    return this.#assets;
  }

  // Git wrapper. Opt-in so the default Workspace graph does not
  // carry isomorphic-git. When configured, it does not require a
  // backend — every supported subcommand reads and writes through
  // the local SQLite-backed VFS. The configured factory decides
  // how the heavy git implementation is loaded.
  //
  // Memoised on a private field so repeated callers share the
  // pack/index cache and resolved modules from the configured
  // implementation.
  get git(): GitClient {
    if (!this.#gitFactory) {
      throw new Error(GIT_NOT_CONFIGURED_MESSAGE);
    }
    if (!this.#git) {
      this.#git = this.#gitFactory({
        ws: this,
        defaultIdentity: this.#defaultGitIdentity,
      });
    }
    return this.#git;
  }

  get artifacts(): ArtifactClient {
    return this.#artifacts;
  }

  get runtime(): WorkspaceRuntime {
    if (!this.#runtime) {
      this.#runtime = new WorkspaceRuntime({
        backends: this.#registeredBackends,
        backendHandle: (id) => this.#backendHandleFor(id),
        resolveBackendId: (id) => this.#resolveBackendId(id) ?? "",
        ...(this.#output === false
          ? {}
          : {
              output: {
                limits: makeOutputLimits(this.#output, "Workspace output"),
                files: new WorkspaceCommandOutputFiles(
                  this.#fs,
                  this.#output.dir ?? DEFAULT_OUTPUT_DIR,
                  this.#output.keep ?? DEFAULT_OUTPUT_KEEP,
                ),
              },
            }),
      });
    }
    return this.#runtime;
  }

  /**
   * Underlying dofs `SQLiteWorkspaceProvider` over the local store.
   *
   * This is the `@platformatic/vfs`-shaped provider — a node:fs
   * surface with full symlink support. Callers that want a
   * `VirtualFileSystem` (e.g. to hand to isomorphic-git) wrap it
   * themselves to keep `@platformatic/vfs` out of this package's
   * dependency tree:
   *
   * ```ts
   * import { create, VirtualProvider } from "@platformatic/vfs";
   * import type { SQLiteWorkspaceProvider } from "@cloudflare/dofs";
   *
   * class Glue extends VirtualProvider {
   *   constructor(private inner: SQLiteWorkspaceProvider) { super(); }
   *   override get readonly()         { return this.inner.readonly; }
   *   override get supportsSymlinks() { return this.inner.supportsSymlinks; }
   *   override get supportsWatch()    { return this.inner.supportsWatch; }
   * }
   * // Forward every node:fs method to `inner` via a
   * // `for (const name of [...]) Object.defineProperty(...)` loop.
   * const vfs = create(new Glue(workspace.provider()));
   * ```
   *
   * Available immediately; doesn't need `ready()` because the
   * provider only reads/writes the local store, not the wire.
   */
  provider(): SQLiteWorkspaceProvider {
    if (!this.#provider) {
      this.#provider = new SQLiteWorkspaceProvider(this.#db, { now: this.#now });
    }
    return this.#provider;
  }

  // ensureMountsIndexed() is the only thing ready() does today;
  // backends connect lazily on first use. The promise is still
  // cached so concurrent ready() calls share one index pass; a
  // failed pass is uncached so the next call retries.
  //
  // Pass an explicit backend id to pre-warm one. Pass
  // `{ all: true }` to dial every backend in parallel — useful
  // from an agent's `onStart` hook.
  ready(options?: string | { all?: boolean }): Promise<void> {
    if (this.#readyPromise === undefined) {
      const pass = this.#mountIndex.ensureIndexed();
      this.#readyPromise = pass;
      pass.catch(() => {
        // A failed mount-index pass must not poison this
        // Workspace forever. The next ready() should re-enter
        // ensureIndexed() and try again.
        if (this.#readyPromise === pass) this.#readyPromise = undefined;
      });
    }
    const indexPromise = this.#readyPromise;
    if (options === undefined) return indexPromise;
    if (typeof options === "string") {
      const id = this.#resolveBackendId(options);
      return (async () => {
        await indexPromise;
        if (!id) return;
        if (this.#moduleBackendsById.has(id)) await this.#moduleHandleFor(id);
        else await this.#handleFor(id);
      })();
    }
    if (options.all) {
      return (async () => {
        await indexPromise;
        await Promise.all([
          ...this.#backends.map((backend) => this.#handleFor(backend.id)),
          ...[...this.#moduleBackendsById.keys()].map((id) => this.#moduleHandleFor(id)),
        ]);
      })();
    }
    return indexPromise;
  }

  // Wrap this workspace in a WorkspaceStub so it can be handed
  // across the Workers-RPC boundary (e.g. returned from a DO RPC
  // method). The stub is a lazy RpcTarget — it doesn't own any
  // resources itself; it just delegates back to this workspace.
  stub(): WorkspaceStub {
    return new WorkspaceStub(this);
  }

  // Sync the local store with a configured backend.
  //
  // push() ships everything the host has written since the last push to
  // that backend; pull() applies everything the backend has produced
  // since the last pull. Both are restartable async iterables: one
  // next() commits one durably checkpointed block, and recreating the
  // iterable resumes from the Workspace's durable cursor.
  //
  // The caller supplies no cursor, no target, and no budget. Block
  // sizing is internal so a caller cannot ask for a block too large to
  // finish inside a Durable Object's CPU allowance.
  //
  // `id` selects which backend to sync with. Omitting it picks the
  // default (the first backend in the list).
  //
  // Workspace never sets or deletes an alarm. Drive these from a
  // request, an alarm, a queue, or a Workflow; see
  // docs/02_sync_protocol.md for the one-step and multi-block handler
  // shapes.
  //
  // Both emit a `workspace.sync.push.block` /
  // `workspace.sync.pull.block` span per committed block, tagged with
  // the resolved backend id and the entry count.
  pull(id?: string): AsyncIterable<SyncProgress> {
    return this.#syncBlocks(id, "pull");
  }

  push(id?: string): AsyncIterable<SyncProgress> {
    return this.#syncBlocks(id, "push");
  }

  // Shared plumbing for both iterables.
  //
  // Each `next()` is wrapped in the backend's mutation FIFO rather than
  // the whole iteration, because holding the FIFO across a yield would
  // block every other mutation for as long as the caller took to come
  // back — and the caller may never come back. Serializing per block
  // keeps the "one mutating sync block per backend and direction"
  // guarantee without making an abandoned iterator wedge the workspace.
  #syncBlocks(id: string | undefined, direction: "pull" | "push"): AsyncIterable<SyncProgress> {
    const span = direction === "pull" ? "workspace.sync.pull.block" : "workspace.sync.push.block";
    return {
      [Symbol.asyncIterator]: (): AsyncIterator<SyncProgress> => {
        let finished = false;
        // Per-iterable rollup for the operation summary. Blocks driven
        // from separate iterables (the eviction case) each report their
        // own partial rollup, which is why the block records carry the
        // operation id: a query can regroup them.
        let blocks = 0;
        let entries = 0;
        let bytes = 0;
        let skipped = 0;
        let worstBlockMs = 0;
        const operationStarted = Date.now();
        return {
          next: async (): Promise<IteratorResult<SyncProgress>> => {
            if (finished) return { done: true, value: undefined };
            const blockStarted = Date.now();
            const result = await this.#serialize(id, (resolvedId) =>
              withSpan(
                this.#observer,
                span,
                { "workspace.sync.backend": resolvedId },
                async (): Promise<IteratorResult<SyncProgress>> => {
                  // A module backend has no sync wire. Report one
                  // complete, empty block so a caller's loop
                  // terminates instead of spinning.
                  if (resolvedId === undefined || this.#moduleBackendsById.has(resolvedId)) {
                    return {
                      done: false,
                      value: emptySyncProgress(resolvedId ?? "none", direction),
                    };
                  }
                  return this.#runWithReconnect(resolvedId, direction, async (handle) => {
                    if (handle.sync === "none") {
                      return {
                        done: false,
                        value: emptySyncProgress(resolvedId, direction),
                      };
                    }
                    // A fresh iterator per attempt, deliberately.
                    // Caching one across calls would pin the RPC stub
                    // it was built with, so a reconnect would retry
                    // against the dead handle. The engine resumes
                    // from durable state, so building a new iterator
                    // costs nothing and is what makes the reconnect
                    // land on the replacement stub.
                    const iterable =
                      direction === "pull"
                        ? pullBlocks(this.#db, handle.rpc.sync, { backend: resolvedId })
                        : pushBlocks(this.#db, handle.rpc.sync, { backend: resolvedId });
                    return iterable[Symbol.asyncIterator]().next();
                  });
                },
                (spanRef, outcome) => {
                  if (!outcome.ok || outcome.value.done) return;
                  spanRef.setAttribute("workspace.sync.entries", outcome.value.value.entries);
                },
              ),
            );
            if (result.done) return result;

            // Structured telemetry alongside the span. The span nests
            // for tracing; this record is what the Workers Observability
            // query API can aggregate, so production runs can answer the
            // CPU-headroom and throughput questions the local benchmarks
            // could only model.
            const blockMs = Date.now() - blockStarted;
            const progress = result.value;
            blocks += 1;
            entries += progress.entries;
            bytes += progress.bytes;
            skipped += progress.skipped;
            worstBlockMs = Math.max(worstBlockMs, blockMs);
            this.#syncLogger.block({
              backend: progress.backend,
              direction: progress.direction,
              mode: progress.mode,
              operationId: progress.operationId,
              generation: progress.generation,
              entries: progress.entries,
              bytes: progress.bytes,
              skipped: progress.skipped,
              complete: progress.complete,
              blockMs,
              cursorRev: progress.cursor.rev,
              targetRev: progress.targetCursor.rev,
            });

            if (progress.complete) {
              finished = true;
              this.#syncLogger.operation({
                backend: progress.backend,
                direction: progress.direction,
                mode: progress.mode,
                operationId: progress.operationId,
                generation: progress.generation,
                blocks,
                entries,
                bytes,
                skipped,
                totalMs: Date.now() - operationStarted,
                worstBlockMs,
                // Every block after the first in one iterable reused
                // the iterator; a caller that recreates the iterable
                // per block reports blocks = 1 each time, so restarts
                // is derivable across records by operation id.
                restarts: 0,
              });
            }
            return result;
          },
          // Breaking out stops local driving. The operation stays
          // pending and its last cursor stays durable, so a later
          // iterable resumes it.
          return: async (): Promise<IteratorResult<SyncProgress>> => {
            finished = true;
            return { done: true, value: undefined };
          },
        };
      },
    };
  }

  #pullResolved(resolvedId: string | undefined, expectedRuntimeId?: string): Promise<ApplyResult> {
    return withSpan(
      this.#observer,
      "workspace.sync.pull",
      { "workspace.sync.backend": resolvedId },
      async () => {
        if (resolvedId === undefined || this.#moduleBackendsById.has(resolvedId)) {
          return { applied: 0, skipped: [] };
        }
        return this.#runWithReconnect(resolvedId, "pull", async (handle) => {
          if (expectedRuntimeId !== undefined) {
            assertExecutionRuntime("post-command sync", expectedRuntimeId, handle.runtimeId);
          }
          if (handle.sync === "none") return { applied: 0, skipped: [] };
          return pullOnce(this.#db, handle.rpc.sync, resolvedId);
        });
      },
      (span, outcome) => {
        if (!outcome.ok) return;
        span.setAttribute("workspace.sync.applied", outcome.value.applied);
        span.setAttribute("workspace.sync.skipped", outcome.value.skipped.length);
      },
    );
  }

  // Capture a durable pull operation for a command whose changes were
  // not drained in-band.
  //
  // This used to persist a host-owned retry intent and rely on the host
  // to set an alarm and call back. The operation row makes that
  // indirection unnecessary: capturing the target here is enough, and
  // any later pull() iteration resumes it from durable state.
  async #capturePendingSync(
    id: string,
    runtimeId?: string,
  ): Promise<{ backend?: string; runtimeId?: string; targetCursor?: ChangeCursor }> {
    return this.#serialize(id, async (resolvedId) => {
      if (resolvedId === undefined || this.#moduleBackendsById.has(resolvedId)) return {};
      const handle = await this.#handleFor(resolvedId);
      if (runtimeId !== undefined) {
        assertExecutionRuntime("post-command sync", runtimeId, handle.runtimeId);
      }
      if (handle.sync === "none") return { backend: resolvedId };

      // Open the operation and fix its target now, so the command's
      // changes are pinned even though nothing drains them yet. A
      // later pull() joins this pending operation rather than
      // capturing a newer target that could race ahead.
      const target = await captureSyncTarget(this.#db, handle.rpc.sync, resolvedId);
      return {
        backend: resolvedId,
        ...(runtimeId === undefined ? {} : { runtimeId }),
        ...(target === undefined ? {} : { targetCursor: target }),
      };
    });
  }

  // Drop and close a cached handle after a transport failure.
  // Matches by identity so a late error from an old operation cannot
  // tear down a replacement that another caller already installed.
  // Cache deletion is synchronous; close() is awaited before retrying
  // so backends with their own handle cache cannot return the same
  // broken session from connect().
  async #invalidateHandle(id: string, handle: BackendHandle): Promise<boolean> {
    if (this.#handles.get(id) !== handle) return false;
    this.#handles.delete(id);
    this.#shells.delete(id);
    this.#commandHandles.delete(id);
    const closing = handle.close().catch(() => undefined);
    this.#disconnecting.set(id, closing);
    try {
      await closing;
    } finally {
      if (this.#disconnecting.get(id) === closing) this.#disconnecting.delete(id);
    }
    return true;
  }

  // Run a backend operation with one reconnect retry. Handle
  // acquisition is inside the loop so a readiness failure and an RPC
  // failure follow the same bounded policy. The default "always"
  // policy assumes an idempotent operation: pushOnce and pullOnce are
  // safe replay boundaries because their durable watermarks advance
  // only after committed work and their apply paths absorb duplicates.
  async #runWithReconnect<T>(
    id: string,
    operation: string,
    op: (handle: BackendHandle) => Promise<T>,
    policy: BackendRetryPolicy = "always",
  ): Promise<T> {
    let firstError: unknown;
    for (let attempt = 0; attempt < 2; attempt++) {
      let handle: BackendHandle | undefined;
      try {
        handle = await this.#handleFor(id);
        return await op(handle);
      } catch (error) {
        if (!isWorkspaceTransportFailure(error)) {
          if (firstError !== undefined && (error as { code?: unknown })?.code === "EEXEC_LOST") {
            throw Object.assign(
              new Error(
                `${safeErrorMessage(error)} Original transport failure: ${safeErrorMessage(firstError)}`,
                { cause: firstError },
              ),
              { name: "WorkspaceExecutionLostError", code: "EEXEC_LOST" },
            );
          }
          throw error;
        }
        if (handle !== undefined) await this.#invalidateHandle(id, handle);

        // A failed connection is always before dispatch. Once an
        // operation reached a handle, only an "always" policy or a
        // local disposed-stub failure makes replay safe.
        const canRetry =
          handle === undefined ||
          policy === "always" ||
          isWorkspacePreDispatchTransportFailure(error);
        if (!canRetry) {
          throw new WorkspaceTransportError(
            `Workspace backend ${JSON.stringify(id)}: ${operation} transport failed; the command may have started and was not replayed: ${safeErrorMessage(error).slice(0, 240)}`,
            { cause: error },
          );
        }
        if (attempt === 0) {
          firstError = error;
          continue;
        }
        const initial = safeErrorMessage(firstError).slice(0, 160);
        const terminal = safeErrorMessage(error).slice(0, 240);
        throw new WorkspaceTransportError(
          `Workspace backend ${JSON.stringify(id)}: ${operation} failed after 1 reconnect retry: initial=${initial}; last=${terminal}`,
          { cause: error },
        );
      }
    }
    throw new Error("unreachable reconnect state");
  }

  // Per-backend mutation FIFO. Public push() / pull() calls and each
  // command's pre-exec push and post-stream pull route through this;
  // the FIFO is not held for the command's lifetime. Reads bypass it
  // entirely. A push to backend A does not block sync on backend B
  // because each id gets its own tail-promise. The undefined id (filesystem-only
  // path through push/pull) shares one slot.
  //
  // Rejections are not contagious: the catch arm here swallows
  // failures so a failing mutation doesn't poison the rest of
  // the queue — the caller still sees the original rejection
  // through the returned promise.
  #serialize<T>(
    id: string | undefined,
    fn: (resolvedId: string | undefined) => Promise<T>,
  ): Promise<T> {
    const resolved = this.#resolveBackendId(id);
    const slot = resolved ?? "";
    const tail = this.#mutationTails.get(slot) ?? Promise.resolve();
    const run = tail.then(
      () => fn(resolved),
      () => fn(resolved),
    );
    this.#mutationTails.set(
      slot,
      run.then(
        () => undefined,
        () => undefined,
      ),
    );
    return run;
  }

  // Resolve an exec / push / pull caller's id argument to a
  // concrete backend id. Returns undefined for a filesystem-only
  // workspace; throws on an unknown id. Omitted ids fall through
  // to the first backend in the list (the default).
  #resolveBackendId(id: string | undefined): string | undefined {
    if (this.#registeredBackends.size === 0) return undefined;
    const target = id ?? this.#defaultBackendId;
    if (target === undefined) return undefined;
    if (!this.#registeredBackends.has(target)) {
      throw new Error(
        `Workspace: no backend with id ${JSON.stringify(target)}. ` +
          `Configured backends: ${[...this.#registeredBackends.keys()].map((key) => JSON.stringify(key)).join(", ") || "<none>"}.`,
      );
    }
    return target;
  }

  async close(): Promise<void> {
    // Close every cached handle in parallel. Drop caches before
    // awaiting so a subsequent ready() / exec sees an empty slate
    // and rebuilds against fresh handles.
    this.#connectionGeneration += 1;
    const handles = [...this.#handles.values()];
    const moduleHandles = [...this.#moduleHandles.values()];
    this.#handles.clear();
    this.#shells.clear();
    this.#commandHandles.clear();
    this.#connecting.clear();
    const disconnecting = [...this.#disconnecting.values()];
    this.#moduleHandles.clear();
    this.#connectingModuleHandles.clear();
    this.#readyPromise = undefined;
    await Promise.all([
      ...disconnecting,
      ...[...handles, ...moduleHandles].map(async (h) => {
        try {
          await h.close?.();
        } catch {
          // close() is best-effort; a transport that's already
          // gone shouldn't take the workspace down with it.
        }
      }),
    ]);
  }

  // Unified backend handle used by the runtime. Module backends
  // return their native handle; command backends are presented
  // through the same interface by an adapter over their
  // CommandExecutor, so the runtime has a single execution path.
  async #backendHandleFor(id: string): Promise<WorkspaceModuleBackendHandle> {
    if (this.#moduleBackendsById.has(id)) return this.#moduleHandleFor(id);
    return this.#commandHandleFor(id);
  }

  // Command adapters are cached per backend, but the CommandExecutor
  // they contain resolves the current ShellRPC for every operation.
  // A pre-exec push can replace the connection, so binding the adapter
  // to the handle that existed before that push would dispatch the
  // command on a stale session.
  async #commandHandleFor(id: string): Promise<WorkspaceModuleBackendHandle> {
    const cached = this.#commandHandles.get(id);
    if (cached) return cached;
    const shell = this.#shellFor(id);
    const adapter: WorkspaceModuleBackendHandle = {
      exec: async (input) => {
        const envelope = await shell.exec(input.source, {
          id: input.id,
          cwd: input.cwd,
          timeoutMs: input.timeoutMs,
          env: input.env,
          stdin: input.stdin,
          sync: input.sync,
        });
        this.#rememberExecutionRuntime(id, envelope.id, envelope.runtimeId);
        return {
          id: envelope.id,
          runtimeId: envelope.runtimeId,
          events: envelope.events as ReadableStream<WorkspaceRuntimeEvent>,
          sync: envelope.sync,
        };
      },
      getExec: async ({ id: execId, after, runtimeId }) => {
        const resume = after === undefined ? "full" : after;
        const expectedRuntimeId =
          runtimeId ?? this.#executionRuntimes.get(this.#executionRuntimeKey(id, execId));
        const envelope = await shell.get(execId, { resume, runtimeId: expectedRuntimeId });
        this.#rememberExecutionRuntime(id, envelope.id, envelope.runtimeId);
        return {
          id: envelope.id,
          runtimeId: envelope.runtimeId,
          events: envelope.events as ReadableStream<WorkspaceRuntimeEvent>,
          sync: envelope.sync,
        };
      },
      killExec: ({ id: execId, signal, runtimeId }) =>
        this.#runExecutionOperation(
          id,
          "shell.killExec",
          execId,
          runtimeId ?? this.#executionRuntimes.get(this.#executionRuntimeKey(id, execId)),
          (shellRpc) => shellRpc.killExec({ id: execId, signal }),
        ),
      disposeExec: async ({ id: execId, runtimeId }) => {
        const key = this.#executionRuntimeKey(id, execId);
        const expectedRuntimeId = runtimeId ?? this.#executionRuntimes.get(key);
        await this.#runExecutionOperation(
          id,
          "shell.disposeExec",
          execId,
          expectedRuntimeId,
          (shellRpc) => shellRpc.disposeExec({ id: execId }),
        );
        this.#executionRuntimes.delete(key, expectedRuntimeId);
      },
    };
    this.#commandHandles.set(id, adapter);
    return adapter;
  }

  #moduleHandleFor(id: string): Promise<WorkspaceModuleBackendHandle> {
    const cached = this.#moduleHandles.get(id);
    if (cached) return Promise.resolve(cached);
    const inflight = this.#connectingModuleHandles.get(id);
    if (inflight) return inflight;
    const backend = this.#moduleBackendsById.get(id);
    if (!backend) {
      return Promise.reject(
        new Error(`Workspace backend ${JSON.stringify(id)} does not execute modules.`),
      );
    }
    const generation = this.#connectionGeneration;
    // oxlint-disable-next-line eslint/prefer-const -- its callbacks read `promise`
    let promise!: Promise<WorkspaceModuleBackendHandle>;
    promise = withSpan(
      this.#observer,
      "workspace.connect",
      { "workspace.backend.id": id, "workspace.backend.type": backend.type },
      () =>
        backend.connect({
          db: this.#db,
          fs: this.#fs,
          git: this.#gitFactory ? this.git : DISABLED_GIT_CLIENT,
          artifacts: this.#artifacts,
          runtime: this.runtime,
          ...(this.#assets === undefined ? {} : { assets: this.#assets }),
        }),
    )
      .then(async (handle) => {
        if (generation !== this.#connectionGeneration) {
          await handle.close?.().catch(() => undefined);
          throw new Error(`Workspace closed while backend ${JSON.stringify(id)} was connecting.`);
        }
        this.#moduleHandles.set(id, handle);
        return handle;
      })
      .finally(() => {
        if (this.#connectingModuleHandles.get(id) === promise) {
          this.#connectingModuleHandles.delete(id);
        }
      });
    this.#connectingModuleHandles.set(id, promise);
    return promise;
  }

  // Lazy backend connect. Concurrent callers for the same id
  // share one in-flight promise. The resolved handle is cached
  // until close() or the backend's `closed` promise fires.
  #handleFor(id: string): Promise<BackendHandle> {
    const cached = this.#handles.get(id);
    if (cached !== undefined) return Promise.resolve(cached);
    const disconnecting = this.#disconnecting.get(id);
    if (disconnecting !== undefined) {
      return disconnecting.then(() => this.#handleFor(id));
    }
    const inflight = this.#connecting.get(id);
    if (inflight !== undefined) return inflight;
    const backend = this.#backendsById.get(id);
    if (backend === undefined) {
      return Promise.reject(new Error(`Workspace: no backend with id ${JSON.stringify(id)}`));
    }
    const generation = this.#connectionGeneration;
    // oxlint-disable-next-line eslint/prefer-const -- its callbacks read `promise`
    let promise!: Promise<BackendHandle>;
    promise = (async () => {
      let handle: BackendHandle | undefined;
      try {
        handle = await withSpan(
          this.#observer,
          "workspace.connect",
          { "workspace.backend.id": id, "workspace.backend.type": backend.type },
          () =>
            backend.connect({
              db: this.#db,
              fs: this.#fs,
              git: this.#gitFactory ? this.git : DISABLED_GIT_CLIENT,
              artifacts: this.#artifacts,
            }),
        );
        if (generation !== this.#connectionGeneration) {
          throw new Error(`Workspace closed while backend ${JSON.stringify(id)} was connecting.`);
        }
        // Reconcile watermarks before publishing the handle. If the
        // remote restarted between our pushes / fetches it has lost
        // state we thought it had; reset the local cursors so the
        // next tick rebaselines.
        //
        // A backend that declares sync: "none" has no remote store
        // to reconcile against; skip the pass entirely.
        if (handle.sync !== "none") {
          await reconcileWatermarks(this.#db, handle.rpc.sync, id);
        }
        if (generation !== this.#connectionGeneration) {
          throw new Error(`Workspace closed while backend ${JSON.stringify(id)} was connecting.`);
        }
        this.#handles.set(id, handle);
        // Watch the transport for mid-session loss. Backends without
        // a `closed` promise (in-process fakes) opt out by omitting
        // it; we only react when it's wired.
        if (handle.closed) {
          void handle.closed
            .catch(() => {})
            .then(() => {
              // Only clear if this handle is still the current one
              // for this id. A close() that already ran will have
              // dropped the entry; a subsequent #handleFor may have
              // installed a new one.
              if (this.#handles.get(id) === handle) {
                this.#handles.delete(id);
                this.#shells.delete(id);
                this.#commandHandles.delete(id);
              }
            });
        }
        return handle;
      } catch (error) {
        // A handle that fails setup was never published, but the
        // backend may still cache it internally. Close it so the next
        // connect attempt cannot return the same broken session.
        if (handle !== undefined && this.#handles.get(id) !== handle) {
          await handle.close().catch(() => undefined);
        }
        throw error;
      }
    })().finally(() => {
      // Always drop this in-flight entry so a failed connect can be
      // retried, without deleting a newer connection started after close().
      if (this.#connecting.get(id) === promise) this.#connecting.delete(id);
    });
    this.#connecting.set(id, promise);
    return promise;
  }

  // Per-backend CommandExecutor, constructed on demand. Exec keeps
  // its pre-command push and spawn on one BackendHandle. If that
  // handle fails before dispatch, the reconnect retry repeats both
  // steps on the replacement so the command cannot skip its push.
  // Other shell operations resolve a handle at call time and bind
  // stream failures to the handle that produced the envelope.
  #shellFor(id: string): CommandExecutor {
    const cached = this.#shells.get(id);
    if (cached !== undefined) return cached;
    const dispatch = (input: Parameters<ShellRPC["exec"]>[0]) =>
      this.#runWithReconnect(
        id,
        "shell.exec",
        async (handle) => {
          let pushed: number;
          try {
            pushed = await this.#pushForExec(id, handle);
          } catch (error) {
            if (!isWorkspaceTransportFailure(error)) throw error;
            throw new WorkspacePreDispatchTransportError(
              `pre-exec push failed: ${safeErrorMessage(error)}`,
              { cause: error },
            );
          }
          const envelope = await spawnShell(handle.rpc.shell, input, this.#observer);
          return {
            pushed,
            envelope: wrapShellEnvelope(
              envelope,
              (error) => this.#onShellError(id, handle, error),
              handle.runtimeId,
            ),
          };
        },
        "pre-dispatch",
      );
    const getDispatch = (input: Parameters<ShellRPC["getExec"]>[0] & { runtimeId?: string }) =>
      this.#runShellEnvelope(
        id,
        "shell.getExec",
        (shell) => shell.getExec({ id: input.id, after: input.after }),
        "always",
        { executionId: input.id, runtimeId: input.runtimeId },
      );
    const rpc: ShellRPC = {
      exec: async (input) => (await dispatch(input)).envelope,
      getExec: (input) => getDispatch(input),
      killExec: (input) =>
        this.#runWithReconnect(id, "shell.killExec", (handle) => handle.rpc.shell.killExec(input)),
      disposeExec: (input) =>
        this.#runWithReconnect(id, "shell.disposeExec", (handle) =>
          handle.rpc.shell.disposeExec(input),
        ),
    };
    const shell = new CommandExecutor(
      rpc,
      {
        push: () => this.#pushForExecDefault(id),
        pull: (runtimeId) => this.#pullForExec(id, runtimeId),
        // A pull that failed in-band already left its operation
        // pending, and its cursor is durable, so the next pull()
        // resumes it with no bookkeeping here. Dialing a fresh handle
        // just to record that would turn a failed command into a
        // second connection attempt.
        onPullPending: undefined,
        onPostExecPending: (runtimeId) => this.#capturePendingSync(id, runtimeId),
      },
      this.#observer,
      dispatch,
      getDispatch,
    );
    this.#shells.set(id, shell);
    return shell;
  }

  #pullForExec(id: string, runtimeId?: string): Promise<ApplyResult> {
    return this.#serialize(id, (resolvedId) => this.#pullResolved(resolvedId, runtimeId));
  }

  // The exec bracket's pre-command push. Distinct from the public
  // push() iterable: the bracket needs the whole local window shipped
  // before the command starts, and an entry count to report on the
  // execution, not a block-at-a-time cursor it would have to drive.
  #pushForExecDefault(id: string): Promise<number> {
    return this.#serialize(id, (resolvedId) =>
      withSpan(
        this.#observer,
        "workspace.sync.push",
        { "workspace.sync.backend": resolvedId },
        async () => {
          if (resolvedId === undefined || this.#moduleBackendsById.has(resolvedId)) return 0;
          return this.#runWithReconnect(resolvedId, "push", async (handle) => {
            if (handle.sync === "none") return 0;
            return pushOnce(this.#db, handle.rpc.sync, resolvedId);
          });
        },
        (span, outcome) => {
          if (outcome.ok) span.setAttribute("workspace.sync.pushed", outcome.value);
        },
      ),
    );
  }

  #pushForExec(id: string, handle: BackendHandle): Promise<number> {
    return this.#serialize(id, (resolvedId) =>
      withSpan(
        this.#observer,
        "workspace.sync.push",
        { "workspace.sync.backend": resolvedId },
        async () => {
          if (resolvedId === undefined || this.#moduleBackendsById.has(resolvedId)) return 0;
          if (handle.sync === "none") return 0;
          return pushOnce(this.#db, handle.rpc.sync, resolvedId);
        },
        (span, outcome) => {
          if (outcome.ok) span.setAttribute("workspace.sync.pushed", outcome.value);
        },
      ),
    );
  }

  #runShellEnvelope(
    id: string,
    operation: string,
    op: (shell: ShellRPC) => ReturnType<ShellRPC["exec"]>,
    policy: BackendRetryPolicy = "always",
    expected?: { executionId: string; runtimeId?: string },
  ): ReturnType<ShellRPC["exec"]> {
    return this.#runWithReconnect(
      id,
      operation,
      async (handle) => {
        if (expected !== undefined) {
          assertExecutionRuntime(expected.executionId, expected.runtimeId, handle.runtimeId);
        }
        const envelope = await op(handle.rpc.shell);
        return wrapShellEnvelope(
          envelope,
          (error) => this.#onShellError(id, handle, error),
          handle.runtimeId,
        );
      },
      policy,
    );
  }

  #runExecutionOperation(
    id: string,
    operation: string,
    executionId: string,
    runtimeId: string | undefined,
    op: (shell: ShellRPC) => Promise<void>,
  ): Promise<void> {
    return this.#runWithReconnect(id, operation, async (handle) => {
      assertExecutionRuntime(executionId, runtimeId, handle.runtimeId);
      await op(handle.rpc.shell);
    });
  }

  #executionRuntimeKey(backendId: string, executionId: string): string {
    return JSON.stringify([backendId, executionId]);
  }

  #rememberExecutionRuntime(
    backendId: string,
    executionId: string,
    runtimeId: string | undefined,
  ): void {
    if (runtimeId !== undefined) {
      this.#executionRuntimes.remember(
        this.#executionRuntimeKey(backendId, executionId),
        runtimeId,
      );
    }
  }

  // Invalidate the cached handle for `id` when a shell-routed RPC
  // fails with a known transport error. Compares the caller's
  // captured handle against the live cache entry so a late-failing
  // operation against an old handle can't clobber a newer one that
  // a concurrent reconnect already installed.
  #onShellError(id: string, handle: BackendHandle, error: unknown): void {
    if (!isWorkspaceTransportFailure(error)) return;
    void this.#invalidateHandle(id, handle);
  }
}

// A backend with no sync wire (a module backend, or a handle that
// reports sync: "none") still has to produce a terminating iterable.
// One complete, empty block lets a caller's `for await` finish rather
// than spin waiting for progress that will never come.
function emptySyncProgress(backend: string, direction: "pull" | "push"): SyncProgress {
  const cursor = { rev: 0, path: null };
  return {
    operationId: "none",
    generation: "none",
    backend,
    direction,
    mode: "entries",
    cursor,
    targetCursor: cursor,
    entries: 0,
    bytes: 0,
    skipped: 0,
    complete: true,
  };
}

function assertExecutionRuntime(
  executionId: string,
  expectedRuntimeId: string | undefined,
  currentRuntimeId: string | undefined,
): void {
  if (expectedRuntimeId === undefined || currentRuntimeId === expectedRuntimeId) return;
  throw Object.assign(
    new Error(
      `Execution ${JSON.stringify(executionId)} was lost when its container runtime was replaced.`,
    ),
    { name: "WorkspaceExecutionLostError", code: "EEXEC_LOST" },
  );
}

// Keep ownership of the original capnweb result envelope while
// replacing its event stream with a transport-aware wrapper. The
// CommandExecutor disposes this local envelope when the stream ends;
// forwarding that disposal releases the real remote envelope exactly
// once.
function wrapShellEnvelope(
  envelope: Awaited<ReturnType<ShellRPC["exec"]>>,
  onError: (error: unknown) => void,
  runtimeId?: string,
): Awaited<ReturnType<ShellRPC["exec"]>> & { runtimeId?: string } {
  let disposed = false;
  const wrapped = {
    id: envelope.id,
    runtimeId,
    events: watchStreamForTransportError(envelope.events, onError),
    [Symbol.dispose]() {
      if (disposed) return;
      disposed = true;
      maybeDispose(envelope);
    },
  };
  return wrapped;
}

// Pass an execution event stream through unchanged, but classify any
// error that tears it down. A transport-classified mid-stream failure
// invalidates the cached backend handle so the next call reconnects,
// matching the invalidation the shell router used to install around a
// command handle's result().
function watchStreamForTransportError<T>(
  events: ReadableStream<T>,
  onError: (error: unknown) => void,
): ReadableStream<T> {
  const reader = events.getReader();
  return new ReadableStream<T>({
    async pull(controller) {
      try {
        const { value, done } = await reader.read();
        if (done) {
          controller.close();
          return;
        }
        controller.enqueue(value);
      } catch (error) {
        onError(error);
        controller.error(error);
      }
    },
    async cancel(reason) {
      await reader.cancel(reason);
    },
  });
}

/**
 * Resolve the session id the artifacts client runs under: the
 * artifacts-specific one when the caller set it, otherwise the
 * workspace's own, and undefined for a namespace-wide client.
 *
 * An omitted or undefined id intentionally asks for the
 * namespace-wide client. An empty id is passed through rather than
 * read as absent, so blank input or code such as
 * `sessionId: value ?? ""` gets the wrapper's
 * `InvalidSessionIdError` instead of widening access. Passing
 * `null` is the explicit opt-out when the workspace has a session
 * id of its own.
 */
function artifactsSessionId(options: WorkspaceOptions): string | null | undefined {
  const configured = options.artifacts?.sessionId;
  if (configured !== undefined) return configured;
  return options.sessionId;
}

function createDisabledArtifactsClient(): ArtifactClient {
  const fail = () => {
    throw new ArtifactError("ENOCONFIG", ARTIFACTS_NOT_CONFIGURED_MESSAGE);
  };
  return {
    sessionId: undefined,
    create: fail,
    get: fail,
    list: fail,
    import: fail,
    delete: fail,
    createToken: fail,
    listTokens: fail,
    getToken: fail,
    revokeToken: fail,
    async cli(input) {
      return runArtifactsCLI(this, input, false);
    },
  } as ArtifactClient;
}
